import { useEffect, useRef } from 'react';
import { features } from '@/mocks/home';

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full px-4 sm:px-6 lg:px-8 py-20 md:py-28 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="reveal-on-scroll font-heading text-3xl sm:text-4xl font-bold mb-4 text-primary-500">
            Everything You Need for Mental Clarity
          </h2>
          <p className="reveal-on-scroll animation-delay-100 text-foreground-600 max-w-xl mx-auto font-body leading-relaxed">
            A complete toolkit for focus, stress management, and cognitive wellness — no medical jargon, no overwhelm.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={`reveal-on-scroll animation-delay-${(index + 1) * 100} group bg-background-50 border border-background-200/70 rounded-2xl p-6 hover:bg-accent-500 hover:border-accent-500 transition-all duration-300 cursor-default`}
            >
              <div className="w-10 h-10 rounded-xl bg-accent-100 group-hover:bg-accent-600 flex items-center justify-center mb-4 transition-colors duration-300">
                <i className={`${feature.icon} text-accent-700 group-hover:text-foreground-950 text-lg transition-colors duration-300`} />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2 text-primary-500 group-hover:text-foreground-950 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-sm text-foreground-600 group-hover:text-foreground-800 leading-relaxed transition-colors duration-300">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}